import React from 'react';
import { Menu } from 'semantic-ui-react';
import { Link, useLocation } from 'react-router-dom';
import history from 'helpers/history';
import { IUser, IPolicyScan } from 'types';
import styles from './styles/navbar.module.scss';
import classnames from 'classnames';
import { HOME_ROUTE, MARKET_SCAN_ROUTE, NOTIFICATIONS_ROUTE, DASHBOARD_ROUTE, EXPLORE_ROUTE } from 'constants/routes';
import Logo from 'components/logo';
import { NotificationPopup } from 'components/notification-list';
import { HelpTooltip } from 'components/tooltip';
import { getNavbarIcon, getNavbarTitle, getPageName } from './utils';
import {
  isMarketScanResultsPage,
  showMSStatusHeader,
  isMSResults,
} from 'containers/layout/utils';
import TestimonialAnimation from 'containers/market-scan/components/TestimonialAnimation';
import { MarketScanIconV2 } from 'components/svgs/MarketScanIconV2';
import { MarketScanInProgressIcon } from 'components/svgs/MarketScanInProgressIcon';
import { HomeIcon } from 'components/svgs/HomeIcon';
import { DashboardIconNew } from 'components/svgs/DashboardIconNew';
import { ExploreIcon } from 'components/svgs/ExploreIcon';
import { Bell } from 'react-feather';
import { isNewMarketScanLifePage } from 'containers/layout/utils';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import NavBarMarkScanOptions from './NavBarMarketScanOptions';
import { isThirdPartyMarketScan } from 'helpers';
import NavBarMarkScanOptionsMobile from './NavBarMarketScanOptionsMobile';

interface INavBarProps {
  openMobile?: boolean;
  user: IUser;
  navbarTitle?: string;
  policyScan?: IPolicyScan;
  openMobileSidebar(): void;
}
interface INavTitleProps {
  className?: string;
  title?: string;
  navbarTitle?: string;
  policyScan?: IPolicyScan;
}
interface IMSHeaderProps {
  className?: string;
  policyScan: IPolicyScan;
  navbarTitle?: string;
}

const MarketScanWaitingPageTitle = ({
  policyScan,
  navbarTitle,
}: {
  navbarTitle?: string;
  policyScan?: IPolicyScan;
}) => {
  return (
    <>
      <div className={styles.msHeaderTitle}>
        <img
          className={styles.navbarIcon}
          src={getNavbarIcon(policyScan, navbarTitle)}
        />
        <p className={styles.navbarTitle}>
          {getNavbarTitle(policyScan, navbarTitle)}
        </p>

        {isMarketScanResultsPage(location.pathname) && (
          <HelpTooltip
            toggleContainerClassName={styles.tooltip}
            position="bottom center"
            className={styles.helpTooltip}
            basic={true}
            content={
              <>
                <b>Important:</b> Comparison table is for information purposes
                only. We try to avoid errors but they may exist. Some
                assumptions may have been necessary to provide you with these
                quotes. Providers and policies shown are not representative of
                the entire market nor are they an indication of any endorsement,
                affiliation or a commercial relationship with Quashed. If you
                use our service, click on a link, express interest in, or
                acquire a policy via Quashed, we may receive remuneration.
                Policies shown are not a recommendation nor are they an
                indication that a policy is the best in the market. You can read
                our FAQs, Privacy Policy and Terms & Conditions for more
                information.
              </>
            }
          />
        )}
        {isMarketScanResultsPage(location.pathname) &&
          [
            INSURANCE_TYPES_NAMES.VEHICLE,
            INSURANCE_TYPES_NAMES.CONTENT,
            INSURANCE_TYPES_NAMES.HOME,
            INSURANCE_TYPES_NAMES.LANDLORD,
            INSURANCE_TYPES_NAMES.HOME_CONTENTS,
            INSURANCE_TYPES_NAMES.MOTORCYCLE,
            INSURANCE_TYPES_NAMES.RV,
          ].includes(policyScan?.policyType || '') &&
          policyScan && (
            <NavBarMarkScanOptions
              showCoverType={
                policyScan.policyType === INSURANCE_TYPES_NAMES.VEHICLE
              }
              policySubTypeName={policyScan.policySubTypeName}
              amount={
                policyScan.policyType === INSURANCE_TYPES_NAMES.LANDLORD
                  ? policyScan.amountHome
                  : policyScan.amount
              }
              excess={
                policyScan.policyType === INSURANCE_TYPES_NAMES.LANDLORD
                  ? policyScan.excessHome
                  : policyScan.excess
              }
              excessContents={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.excessContents
              }
              excessHome={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.excessHome
              }
              amountContents={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.amountContents
              }
              amountHome={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.amountHome
              }
              thirdPartyScan={isThirdPartyMarketScan(policyScan)}
            />
          )}
      </div>
      <div className={styles.msHeaderMobileOptions}>
        {isMarketScanResultsPage(location.pathname) &&
          [
            INSURANCE_TYPES_NAMES.VEHICLE,
            INSURANCE_TYPES_NAMES.CONTENT,
            INSURANCE_TYPES_NAMES.HOME,
            INSURANCE_TYPES_NAMES.LANDLORD,
            INSURANCE_TYPES_NAMES.HOME_CONTENTS,
            INSURANCE_TYPES_NAMES.MOTORCYCLE,
            INSURANCE_TYPES_NAMES.RV,
          ].includes(policyScan?.policyType || '') &&
          policyScan && (
            <NavBarMarkScanOptionsMobile
              showCoverType={
                policyScan.policyType === INSURANCE_TYPES_NAMES.VEHICLE
              }
              policySubTypeName={policyScan.policySubTypeName}
              amount={
                policyScan.policyType === INSURANCE_TYPES_NAMES.LANDLORD
                  ? policyScan.amountHome
                  : policyScan.amount
              }
              excess={
                policyScan.policyType === INSURANCE_TYPES_NAMES.LANDLORD
                  ? policyScan.excessHome
                  : policyScan.excess
              }
              excessContents={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.excessContents
              }
              excessHome={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.excessHome
              }
              amountContents={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.amountContents
              }
              amountHome={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.amountHome
              }
              thirdPartyScan={isThirdPartyMarketScan(policyScan)}
            />
          )}
      </div>
    </>
  );
};

export const MarketScanWaitingPageHeader = ({
  className,
  policyScan,
  navbarTitle,
}: IMSHeaderProps) => {
  return (
    <div className={styles.msHeaderContainer}>
      <TestimonialAnimation />
      <MarketScanWaitingPageTitle
        policyScan={policyScan}
        navbarTitle={navbarTitle}
      />
    </div>
  );
};

export const NavTitle = ({
  className,
  title,
  navbarTitle,
  policyScan,
}: INavTitleProps) => {
  const location = useLocation();
  let name = title || getPageName(location);
  let showUnderline = true;
  let wrapperClass = '';

  const isHomePage = location.pathname === HOME_ROUTE;
  const isDashboardPage = location.pathname === DASHBOARD_ROUTE;
  const isMarketScanPage = location.pathname === MARKET_SCAN_ROUTE;
  const isExplorePage = location.pathname.includes(EXPLORE_ROUTE);
  const isNotificationsPage = location.pathname === NOTIFICATIONS_ROUTE;

  if (isHomePage) {
    name = (
      <div className={styles.pageTitle}>
        <HomeIcon className={styles.pageTitleIcon} />
        <span className={styles.pageTitleText}>Home</span>
      </div>
    );
    wrapperClass = styles.pageTitleWrapper;
  }

  if (isDashboardPage) {
    name = (
      <div className={styles.pageTitle}>
        <DashboardIconNew className={styles.pageTitleIcon} />
        <span className={styles.pageTitleText}>My Dashboard</span>
      </div>
    );
    wrapperClass = styles.pageTitleWrapper;
  }

  if (isMarketScanPage) {
    name = (
      <div className={styles.pageTitle}>
        <MarketScanIconV2 className={styles.pageTitleIcon} />
        <span className={styles.pageTitleText}>Market Scan</span>
      </div>
    );
    wrapperClass = styles.pageTitleWrapper;
  }

  if (isExplorePage) {
    name = (
      <div className={styles.pageTitle}>
        <ExploreIcon className={styles.pageTitleIcon} />
        <span className={styles.pageTitleText}>Explore Insurance</span>
      </div>
    );
    wrapperClass = styles.pageTitleWrapper;
  }

  if (isNotificationsPage) {
    name = (
      <div className={styles.pageTitle}>
        <Bell className={styles.pageTitleIcon} />
        <span className={styles.pageTitleText}>Notifications</span>
      </div>
    );
    wrapperClass = styles.pageTitleWrapper;
  }

  if (isNewMarketScanLifePage(location.pathname)) {
    name = (
      <div className={styles.marketScanTitle}>
        {navbarTitle ? <MarketScanInProgressIcon /> : <MarketScanIconV2 />}
        {navbarTitle || 'Market Scan'}
      </div>
    );
    showUnderline = false;
  }

  if (isMSResults(location.pathname)) {
    name = (
      <MarketScanWaitingPageTitle
        policyScan={policyScan}
        navbarTitle={navbarTitle}
      />
    );
    showUnderline = false;
  }

  return (
    <div
      className={classnames(
        styles.title,
        className,
        wrapperClass,
        isMSResults(location.pathname) && styles.noPadding,
      )}
    >
      <span>{name}</span>
      {showUnderline ? <div className={styles.bar} /> : null}
    </div>
  );
};

const NavBar = ({
  openMobile,

  openMobileSidebar,
  navbarTitle,
  policyScan,
}: INavBarProps) => {
  return (
    <Menu
      className={classnames(styles.navbar)}
      pointing={true}
      secondary={true}
    >
      <Menu.Item className={styles.hamburger} position="left">
        <div
          className={classnames(styles.burger, openMobile && styles.active)}
          onClick={openMobileSidebar}
          data-ignore-outside-clicks={true}
        />
      </Menu.Item>
      <Menu.Item as={Link} header={true} className={styles.brand} to="/">
        <Logo />
      </Menu.Item>
      <Menu.Item className={styles.hamburger} position="right">
        <NotificationPopup
          className={styles.mobileNotif}
          onOpen={() => history.push(NOTIFICATIONS_ROUTE)}
          isMobile={true}
        />
      </Menu.Item>
      {!showMSStatusHeader(location.pathname) ? (
        <NavTitle policyScan={policyScan} navbarTitle={navbarTitle} />
      ) : (
        <MarketScanWaitingPageTitle
          policyScan={policyScan}
          navbarTitle={navbarTitle}
        />
      )}
      <Menu.Item
        className={classnames(
          styles.right,
          showMSStatusHeader(location.pathname) && styles.msWaitingNotification,
        )}
        position="right"
      >
        <NotificationPopup
          popupClassName={styles.notification}
          isMobile={false}
        />
      </Menu.Item>
    </Menu>
  );
};

export default NavBar;
